export const parseWordText = (value, limit = 200) => {
  const seen = new Set();
  const words = [];
  String(value || '').split(/[\n,;，；]+/).forEach((raw) => {
    const word = raw.trim().replace(/^["'“”‘’.,!?]+|["'“”‘’.,!?]+$/g, '').replace(/\s+/g, ' ');
    const key = word.toLocaleLowerCase();
    if (!word || seen.has(key) || words.length >= limit) return;
    seen.add(key);
    words.push(word);
  });
  return words;
};

export const mergeWords = (currentText, additions) => (
  parseWordText([
    currentText,
    ...(additions || []).map((item) => (
      typeof item === 'string' ? item : (item.display_label || item.word || '')
    )),
  ].join('\n')).join('\n')
);

export const shuffle = (values) => {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

export const normalizeAnswer = (value) => String(value || '')
  .trim()
  .toLocaleLowerCase()
  .replace(/[’‘]/g, "'")
  .replace(/\s+/g, ' ');

export const suddenTimeLimit = (streak) => (
  Math.round(2000 + (3000 * Math.exp(-Math.max(0, streak) / 12)))
);

export const MEANING_SPRINT_BASE_MS = 7000;

export const senseKey = (item) => `${String(item?.word || '').trim().toLocaleLowerCase()}::${item?.sense_id || 'primary'}`;

export const groupSelfCheckItems = (items = [], maxSenses = 3) => {
  const groups = [];
  const byWord = new Map();
  for (const item of items) {
    const word = String(item?.word || '').trim();
    if (!word) continue;
    const wordKey = word.toLocaleLowerCase();
    let group = byWord.get(wordKey);
    if (!group) {
      group = { word, key: wordKey, senses: [] };
      byWord.set(wordKey, group);
      groups.push(group);
    }
    if (
      group.senses.length < maxSenses
      && !group.senses.some((sense) => senseKey(sense) === senseKey(item))
    ) {
      group.senses.push(item);
    }
  }
  return groups.map((group) => ({
    ...group,
    sense_ids: group.senses.map((sense) => sense.sense_id || 'primary'),
    display_label: group.senses.length === 1
      ? (group.senses[0].display_label || group.word)
      : `${group.word} (${group.senses.map((sense) => sense.definition_cn || sense.definition_en).join(' / ')})`,
  }));
};

export const groupIdentity = (group) => (
  `${group.key || String(group.word || '').toLocaleLowerCase()}::${(group.sense_ids || []).slice().sort().join('|')}`
);

export const buildGroupMeaningChoices = (group, selectedItems, distractorPool, count = 4) => {
  const correct = (group?.senses || []).map((sense) => ({
    key: senseKey(sense),
    label: sense.definition_cn || sense.definition_en,
    correct: true,
  })).filter((choice) => choice.label);
  const correctKeys = new Set(correct.map((choice) => choice.key));
  const seenLabels = new Set(correct.map((choice) => String(choice.label).trim()));
  const candidates = [...selectedItems, ...distractorPool]
    .filter((item) => !correctKeys.has(senseKey(item)))
    .filter((item) => (item.definition_cn || item.definition_en))
    .sort((a, b) => {
      const targetPos = group?.senses?.[0]?.pos;
      const aSame = a.pos && targetPos && a.pos === targetPos ? 1 : 0;
      const bSame = b.pos && targetPos && b.pos === targetPos ? 1 : 0;
      return bSame - aSame || Math.random() - 0.5;
    });
  const choices = [...correct];
  for (const item of candidates) {
    const label = String(item.definition_cn || item.definition_en).trim();
    if (!label || seenLabels.has(label)) continue;
    seenLabels.add(label);
    choices.push({ key: senseKey(item), label, correct: false });
    if (choices.length >= count) break;
  }
  return shuffle(choices);
};

export const updateMeaningSelection = (current, key, multiple = false) => {
  if (!multiple) return new Set([key]);
  const next = new Set(current);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
};

export const meaningTimeLimit = (group, baseMs = MEANING_SPRINT_BASE_MS) => (
  baseMs + Math.max(0, Math.min(3, group?.senses?.length || 1) - 1) * 1000
);

export const buildMeaningChoices = (target, selectedItems, distractorPool, count = 4) => {
  const answer = target.definition_cn || target.definition_en;
  const candidates = [...selectedItems, ...distractorPool]
    .filter((item) => item.word !== target.word)
    .filter((item) => (item.definition_cn || item.definition_en))
    .sort((a, b) => {
      const aSame = a.pos && target.pos && a.pos === target.pos ? 1 : 0;
      const bSame = b.pos && target.pos && b.pos === target.pos ? 1 : 0;
      return bSame - aSame || Math.random() - 0.5;
    });
  const seen = new Set([String(answer).trim()]);
  const choices = [answer];
  for (const item of candidates) {
    const definition = String(item.definition_cn || item.definition_en).trim();
    if (!definition || seen.has(definition)) continue;
    seen.add(definition);
    choices.push(definition);
    if (choices.length >= count) break;
  }
  return shuffle(choices);
};

export const formatDuration = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.round(Number(milliseconds || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const diffCharacters = (expected, actual) => {
  const source = [...String(expected || '')];
  const input = [...String(actual || '')];
  const rows = Array.from({ length: source.length + 1 }, () => Array(input.length + 1).fill(0));
  for (let i = source.length - 1; i >= 0; i -= 1) {
    for (let j = input.length - 1; j >= 0; j -= 1) {
      rows[i][j] = source[i].toLowerCase() === input[j].toLowerCase()
        ? rows[i + 1][j + 1] + 1
        : Math.max(rows[i + 1][j], rows[i][j + 1]);
    }
  }
  const parts = [];
  let i = 0;
  let j = 0;
  while (i < source.length || j < input.length) {
    if (i < source.length && j < input.length && source[i].toLowerCase() === input[j].toLowerCase()) {
      parts.push({ type: 'match', value: source[i] });
      i += 1;
      j += 1;
    } else if (j < input.length && (i === source.length || rows[i][j + 1] >= rows[i + 1][j])) {
      parts.push({ type: 'extra', value: input[j] });
      j += 1;
    } else {
      parts.push({ type: 'missing', value: source[i] });
      i += 1;
    }
  }
  return parts;
};
